import { Box, Typography, Stack, alpha } from "@mui/material";
import { motion } from "framer-motion";
import { 
  CheckCircle, 
  ArrowForward, 
  TrendingUp, 
  FlashOn, 
  Shield, 
  Speed, 
  Language,
  Star 
} from "@mui/icons-material";
import HeroButtons from "./HeroButtons";

const HeroContent = () => {
  // Staggered animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.5, 
        ease: [0.25, 0.46, 0.45, 0.94] 
      } 
    },
  };

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 2,
      }}
    >
      {/* Enhanced Glow Effects - Reduced */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "5%",
          width: "60%",
          height: "60%",
          background: "radial-gradient(circle, rgba(200,169,107,0.08) 0%, rgba(200,169,107,0.02) 40%, transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }}
      />
      
      {/* Secondary glow - Reduced */}
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "10%",
          width: "40%",
          height: "40%",
          background: "radial-gradient(circle, rgba(200,169,107,0.04) 0%, transparent 70%)",
          filter: "blur(30px)",
          pointerEvents: "none",
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Premium Badge with Animation - Reduced spacing */}
        <motion.div variants={itemVariants}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              display: "inline-flex",
              background: alpha("#C8A96B", 0.08),
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(200,169,107,0.3)",
              borderRadius: "100px",
              px: 2,
              py: 0.75,
              mb: 3,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: "linear-gradient(90deg, transparent, rgba(200,169,107,0.2), transparent)",
                animation: "shimmer 3s infinite",
              },
              "@keyframes shimmer": {
                "0%": { left: "-100%" },
                "100%": { left: "100%" },
              },
            }}
          >
            <FlashOn sx={{ color: "#C8A96B", fontSize: 14 }} />
            <Typography
              sx={{
                color: "#C8A96B",
                letterSpacing: 3,
                fontSize: { xs: "0.6rem", md: "0.65rem" },
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              DIRECT ACCESS TO LEADERSHIP
            </Typography>
          </Stack>
        </motion.div>

        {/* Main Heading with Reduced Font Size */}
        <motion.div variants={itemVariants}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              color: "#fff",
              fontSize: {
                xs: "2rem",
                sm: "2.8rem",
                md: "3.5rem",
                lg: "4rem",
              },
              lineHeight: 1.1,
              mb: 2.5,
              letterSpacing: "-0.02em",
              textShadow: "0 1px 10px rgba(0,0,0,0.2)",
              "& .highlight": {
                background: "linear-gradient(135deg, #C8A96B 0%, #E8D48B 50%, #C8A96B 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -6,
                  left: 0,
                  width: "100%",
                  height: "2px",
                  background: "linear-gradient(90deg, #C8A96B, transparent)",
                  borderRadius: "2px",
                },
              },
            }}
          >
            No Gatekeepers.
            <br />
            <span className="highlight">No middlemen.</span>
          </Typography>
        </motion.div>

        {/* Subtitle with Reduced Font Size */}
        <motion.div variants={itemVariants}>
          <Typography
            sx={{
              color: alpha("#FFFFFF", 0.85),
              fontSize: {
                xs: "0.9rem",
                md: "1rem",
                lg: "1.1rem",
              },
              lineHeight: 1.5,
              mb: 2.5,
              maxWidth: "550px",
              fontWeight: 400,
            }}
          >
            Direct communication with executive leadership worldwide.
          </Typography>
          
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              background: alpha("#C8A96B", 0.06),
              borderRadius: "6px",
              padding: "6px 12px",
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                backgroundColor: "#C8A96B",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.5, transform: "scale(1.2)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
            <Typography
              sx={{
                color: "#C8A96B",
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.3px",
              }}
            >
              No waiting. Direct access.
            </Typography>
          </Box>
        </motion.div>

        {/* Call to Action Buttons */}
        <motion.div variants={itemVariants}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.5}
            sx={{
              mb: 4,
            }}
          >
            <HeroButtons />
          </Stack>
        </motion.div>

        {/* Trust Indicators - Compact */}
        <motion.div variants={itemVariants}>
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{
              pt: 2.5,
              borderTop: `1px solid ${alpha("#C8A96B", 0.12)}`,
              display: "inline-flex",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#4CAF50",
                  boxShadow: "0 0 6px #4CAF50",
                  animation: "pulse 2s infinite",
                }}
              />
              <Typography
                sx={{
                  color: alpha("#FFFFFF", 0.65),
                  fontSize: "0.7rem",
                  fontWeight: 500,
                }}
              >
                Live Support
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#C8A96B",
                  boxShadow: "0 0 6px rgba(200,169,107,0.5)",
                }}
              />
              <Typography
                sx={{
                  color: alpha("#FFFFFF", 0.65),
                  fontSize: "0.7rem",
                  fontWeight: 500,
                }}
              >
                24/7 Availability
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Star sx={{ color: "#C8A96B", fontSize: 12 }} />
              <Typography
                sx={{
                  color: alpha("#FFFFFF", 0.65),
                  fontSize: "0.7rem",
                  fontWeight: 500,
                }}
              >
                Trusted Globally
              </Typography>
            </Box>
          </Stack>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default HeroContent;